import { useState } from 'react';
import { Share, PlusSquare, MoreVertical, MonitorDown, Check, Copy, Compass, Ellipsis } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { InstallBrowser } from '@/pwa/installContext';

type Step = { icon: JSX.Element; text: string };

export function stepsFor(browser: InstallBrowser): Step[] {
  switch (browser) {
    case 'ios-safari':
      return [
        { icon: <Share className="h-5 w-5" />, text: 'Toque em Compartilhar: na barra de baixo do Safari ou, se não aparecer, no botão "…" ao lado do endereço.' },
        { icon: <PlusSquare className="h-5 w-5" />, text: 'Role a lista e escolha "Adicionar à Tela de Início".' },
        { icon: <Check className="h-5 w-5" />, text: 'Toque em "Adicionar". O ícone do AutoFluxo aparece na tela inicial.' },
      ];
    case 'ios-other':
      return [
        { icon: <Share className="h-5 w-5" />, text: 'Toque no botão de compartilhar do navegador (no topo, ao lado do endereço).' },
        { icon: <PlusSquare className="h-5 w-5" />, text: 'Escolha "Adicionar à Tela de Início". Se não aparecer, abra o site no Safari.' },
        { icon: <Check className="h-5 w-5" />, text: 'Toque em "Adicionar". O ícone aparece na tela inicial.' },
      ];
    case 'android':
      return [
        { icon: <MoreVertical className="h-5 w-5" />, text: 'Toque no menu ⋮ do navegador, no canto de cima.' },
        { icon: <MonitorDown className="h-5 w-5" />, text: 'Escolha "Instalar app" ou "Adicionar à tela inicial".' },
        { icon: <Check className="h-5 w-5" />, text: 'Confirme em "Instalar". O ícone aparece na tela do celular.' },
      ];
    case 'in-app':
      return [
        { icon: <Ellipsis className="h-5 w-5" />, text: 'Você está no navegador do WhatsApp/Instagram/Facebook, que não instala apps. Toque no menu (… ou ⋮).' },
        { icon: <Compass className="h-5 w-5" />, text: 'Escolha "Abrir no navegador" (Safari ou Chrome). Ou copie o link e cole no Safari/Chrome.' },
        { icon: <PlusSquare className="h-5 w-5" />, text: 'No Safari/Chrome, toque de novo em "Instalar o AutoFluxo".' },
      ];
    default:
      return [
        { icon: <MonitorDown className="h-5 w-5" />, text: 'Clique no ícone de instalar, na barra de endereço do navegador.' },
        { icon: <MoreVertical className="h-5 w-5" />, text: 'Se não aparecer, abra o menu do navegador e escolha "Instalar".' },
        { icon: <Check className="h-5 w-5" />, text: 'Confirme. O aplicativo abre em janela própria.' },
      ];
  }
}

/** Passo a passo com destaque animado no passo atual. */
export function InstallSteps({ browser }: { browser: InstallBrowser }) {
  const steps = stepsFor(browser);
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-3">
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-lg border border-border bg-card p-3 text-sm text-foreground animate-fade-in"
            style={{ animationDelay: `${i * 180}ms`, animationFillMode: 'both' }}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              {s.icon}
            </span>
            <span className="pt-1"><b className="mr-1 text-primary">{i + 1}.</b>{s.text}</span>
          </li>
        ))}
      </ol>
      {browser === 'in-app' && (
        <Button
          variant="outline"
          className="w-full text-foreground border-border hover:bg-muted hover:text-foreground"
          onClick={async () => {
            try { await navigator.clipboard.writeText(window.location.origin); setCopied(true); } catch { /* ignore */ }
          }}
        >
          <Copy className="mr-2 h-4 w-4" />{copied ? 'Link copiado' : 'Copiar link'}
        </Button>
      )}
    </div>
  );
}
